import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { vendorDepositApi } from '../../api/vendorDepositApi';
import { addVendorExpense, getVendorExpenseById, updateVendorExpense } from '../../api/vendorExpenseApi';
import { toast } from 'react-toastify';
import apiClient from "../../api/axios";

const AddVendorExpense = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { expense_id } = useParams(); // for edit mode
  const [vendors, setVendors] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [vendorCommission, setVendorCommission] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);

  const [formData, setFormData] = useState({
    vendor_id: '',
    deposit_id: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().slice(0, 10),
  });

  // URL query params
  const urlParams = new URLSearchParams(location.search);
  const depositIdFromUrl = urlParams.get('deposit_id');

  // State passed from VendorDepositCash (for Edit)
  const passedState = location.state || {};
  const expenseFromState = passedState.expense || null;
  const depositIdFromState = passedState.depositId || null;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [vendorList, depositList, settingsRes] = await Promise.all([
          vendorDepositApi.getVendors(),
          vendorDepositApi.getDeposits(),
          apiClient.get("/admin/admin-settings")
        ]);

        setVendors(vendorList.data || []);
        setDeposits(depositList.data || []);

        // vendor commission default setting
        const settings = settingsRes.data?.data || {};
        const defaultCommission = parseFloat(settings["delivery.vendor_commission"] || 0);

        let initialForm = { ...formData };
        let initialCommission = defaultCommission;
        let initialFinalAmount = 0;

        if (expenseFromState) {
          setIsEditing(true);

          const commission = parseFloat(expenseFromState.commission_percent || defaultCommission);
          const rawAmount = parseFloat(expenseFromState.raw_amount || 0);
          const finalAmount = parseFloat(expenseFromState.amount || 0);

          initialForm = {
            vendor_id: expenseFromState.vendor_id?.toString() || '',
            deposit_id: expenseFromState.deposit_id?.toString() || '',
            amount: rawAmount,                   // raw_amount in input
            description: expenseFromState.description || '',
            expense_date: expenseFromState.expense_date
              ? new Date(expenseFromState.expense_date).toISOString().slice(0, 10)
              : new Date().toISOString().slice(0, 10),
          };

          initialCommission = commission;
          initialFinalAmount = finalAmount;     // final_amount from backend
        } else if (expense_id) {
          setIsEditing(true);
          const res = await getVendorExpenseById(expense_id);
          if (res.success) {
            const expense = res.data;

            const commission = parseFloat(expense.commission_percent || defaultCommission);
            const rawAmount = parseFloat(expense.raw_amount || 0);
            const finalAmount = parseFloat(expense.amount || 0);

            initialForm = {
              vendor_id: expense.vendor_id?.toString() || '',
              deposit_id: expense.deposit_id?.toString() || '',
              amount: rawAmount,                  // raw_amount in input
              description: expense.description || '',
              expense_date: expense.expense_date
                ? new Date(expense.expense_date).toISOString().slice(0, 10)
                : new Date().toISOString().slice(0, 10),
            };

            initialCommission = commission;
            initialFinalAmount = finalAmount;     // final_amount from backend
          }
        } else if (depositIdFromUrl || depositIdFromState) {
          const selectedDepositId = depositIdFromState || depositIdFromUrl;
          const selectedDeposit = depositList.data?.find(
            dep => dep.deposit_id.toString() === selectedDepositId
          );
          if (selectedDeposit) {
            initialForm.deposit_id = selectedDepositId;
            initialForm.vendor_id = selectedDeposit.vendor_id?.toString() || '';
          }
        }

        const selectedDeposit = depositList.data?.find(
          d => d.deposit_id?.toString() === initialForm.deposit_id
        );
        if (selectedDeposit?.currency_symbol) {
          setCurrencySymbol(selectedDeposit.currency_symbol);
        }

        // set all states
        setFormData(initialForm);
        setVendorCommission(initialCommission);
        setFinalAmount(initialFinalAmount);

      } catch (error) {
        toast.error('Failed to load vendors, deposits, or settings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense_id, depositIdFromUrl, expenseFromState, depositIdFromState]);

    const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      //  deposit select time currency set 
      if (name === "deposit_id") {
        const selectedDeposit = deposits.find(
          d => d.deposit_id?.toString() === value
        );

        if (selectedDeposit?.currency_symbol) {
          setCurrencySymbol(selectedDeposit.currency_symbol);
        }
      }

      return updated;
    });
  };
  useEffect(() => {
    const amt = parseFloat(formData.amount) || 0;
    const vc = parseFloat(vendorCommission) || 0;

    const final = amt - (amt * vc / 100);
    setFinalAmount(final.toFixed(2));
  }, [formData.amount, vendorCommission]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendor_id || !formData.amount || !formData.deposit_id || !formData.expense_date) {
      return toast.error('Please fill all required fields');
    }

    setSubmitting(true);
    try {
        const payload = { 
          ...formData, 
          amount: parseFloat(finalAmount), 
          raw_amount: parseFloat(formData.amount),
          commission_percent: parseFloat(vendorCommission),
        };

      // Correctly handle expense_id from params or state
      const idToUpdate = isEditing ? (expense_id || expenseFromState?.vendor_expense_id) : null;

      if (isEditing && idToUpdate) {
        await updateVendorExpense(idToUpdate, payload);
        toast.success('Expense updated successfully!');
      } else {
        await addVendorExpense(payload);
        toast.success('Expense added successfully!');
      }

      navigate('/vendordepositcash');
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <h2 className="mb-4">{isEditing ? 'Edit Vendor Expense' : 'Add Vendor Expense'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="row g-3">

          {/* Vendor */}
          <div className="col-md-6">
            <label className="form-label">Vendor <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="vendor_id"
              value={formData.vendor_id}
              onChange={handleChange}
              required
              disabled={loading || isEditing}
            >
              <option value="" disabled>{loading ? 'Loading vendors...' : 'Select a vendor'}</option>
              {vendors.map(vendor => (
                <option key={vendor.vendor_id} value={vendor.vendor_id}>
                  {vendor.vendor_name}
                </option>
              ))}
            </select>
          </div>

          {/* Deposit */}
          <div className="col-md-6">
            <label className="form-label">Deposit <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="deposit_id"
              value={formData.deposit_id}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="" disabled>Select a deposit</option>
              {deposits
                .filter(dep => dep.vendor_id?.toString() === formData.vendor_id)
                .filter(dep => {
                  const balance = typeof dep.balance !== 'undefined'
                    ? parseFloat(dep.balance)
                    : (parseFloat(dep.petty_cash) - parseFloat(dep.expense || 0));
                  return balance > 0 || dep.deposit_id.toString() === formData.deposit_id;
                })
                .map(dep => {
                  const balance = typeof dep.balance !== 'undefined'
                    ? parseFloat(dep.balance)
                    : (parseFloat(dep.petty_cash) - parseFloat(dep.expense || 0));
                  return (
                    <option key={dep.deposit_id} value={dep.deposit_id}>
                     {dep.deposit_id} - {dep.deposit_date} 
                    ({(dep.currency_symbol ?? currencySymbol) || ""} {dep.petty_cash}) 
                    | Balance: {(dep.currency_symbol ?? currencySymbol) || ""} {balance.toFixed(2)}
                    </option>
                  );
                })}
            </select>
          </div>

          {/* Amount */}
          <div className="col-md-6">
            <label className="form-label">Amount <span className="text-danger">*</span></label>
            <div className="input-group">    
              <span className="input-group-text">
                {currencySymbol || ""}
              </span>
              <input
                type="number"
                className="form-control"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                required
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Commission */}
          <div className="col-md-3">
            <label className="form-label">Commission (%)</label>
            <input
              type="number"
              className="form-control"
              value={vendorCommission}
              readOnly
            />
            {formData.amount && (
              <small className="text-muted">
                Commission deduction: {vendorCommission}% → 
                {currencySymbol} {(parseFloat(formData.amount) - finalAmount).toFixed(2)}
              </small>
            )}
          </div>

          {/* Final Amount */}
          <div className="col-md-3">
            <label className="form-label">Final Amount</label>
            <input
              type="text"
              className="form-control"
              value={`${currencySymbol || ""} ${finalAmount}`}
              readOnly
            />
          </div>

          {/* Date */}
          <div className="col-md-6">
            <label className="form-label">Date <span className="text-danger">*</span></label>
            <input
              type="date"
              className="form-control"
              name="expense_date"
              value={formData.expense_date}
              onChange={handleChange}
              required
            />
          </div>

          {/* Description */}
          <div className="col-md-6">
            <label className="form-label">Description</label>
            <input
              type="text"
              className="form-control"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description (optional)"
            />
          </div>

          {/* Buttons */}
          <div className="col-12 d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={() => navigate(-1)} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (isEditing ? 'Updating...' : 'Saving...') : (isEditing ? 'Update' : 'Save')}
            </button>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AddVendorExpense;
